require('intersection-observer');
const Emitter = require('tiny-emitter');

/*
 * Get the transform property for the current browser so we can set the style properly
 */
const transformProp = (function () {
  var testEl = document.createElement('div');
  if (testEl.style.transform === null) {
    var vendors = ['Webkit', 'Moz', 'ms'];
    for (var vendor in vendors) {
      if (testEl.style[vendors[vendor] + 'Transform'] !== undefined) {
        return vendors[vendor] + 'Transform';
      }
    }
  }
  return 'transform';
})();
const supportPageOffset = window.pageXOffset !== undefined;
const isCSS1Compat = (document.compatMode || '') === 'CSS1Compat';

export default class ParallaxItem extends Emitter {
  constructor(element, speed) {
    super();
    this.element = element;
    this.speed = speed;
    this.isVisible = false;
    this.isUpdating = false;
    this.isRecalculating = false;
    this.isAbove = false;
    this.isBelow = false;
    this.yTranslate = 0;
    this.observer = undefined;
    this.intersect = this.intersect.bind(this);
    this.y = this.getScrollPos();
    this.createObserver();
    this.calculateDimensions();
  }

  /*
   * Calculates values needed to position this parallaxing item though it's life cycle
   *
   * this.midPoint: the scroll position where the element is vertically centered in the viewport
   * this.maxTranslate: maximum amount the element can move while remaining visible in the viewport
   */
  calculateDimensions() {
    this.isRecalculating = true;
    this.stopObservation();
    this.setStyle(0);
    this.y = this.getScrollPos();
    this.elementTop = this.element.getBoundingClientRect().top + this.y;
    this.elementHeight = this.element.offsetHeight;
    this.viewportHeight = document.documentElement.clientHeight;
    this.midPoint = this.elementTop + this.elementHeight / 2 - this.viewportHeight / 2;

    const modifier = Math.abs(this.speed) * 100;
    let n = this.midPoint - modifier;
    while (n > 0) {
      let scrollPos = n + this.viewportHeight;
      let scrollOffset = n - this.midPoint;
      let yTranslation = Math.round(scrollOffset * this.speed);
      let translatedElement = this.elementTop + yTranslation;
      if (scrollPos <= translatedElement) {
        this.maxTranslate = yTranslation;
        break;
      }
      n -= modifier;
    }
    this.isRecalculating = false;
    this.reposition();
  }

  getScrollPos() {
    return supportPageOffset
      ? window.pageYOffset
      : isCSS1Compat
      ? document.documentElement.scrollTop
      : document.body.scrollTop;
  }

  createObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: [0, 1]
    };
    this.observer = new IntersectionObserver(this.intersect, options);
  }

  startObservation() {
    this.observer.observe(this.element);
  }

  stopObservation() {
    this.observer.unobserve(this.element);
  }

  /*
   * Move element to expected position on init or after resize
   */
  reposition() {
    this.updateTransform(this.y);
    if (this.speed < 0) {
      if (this.yTranslate < 0) {
        if (this.yTranslate < -this.maxTranslate) {
          this.setStyle(-this.maxTranslate);
        }
      } else {
        if (this.yTranslate > this.maxTranslate) {
          this.setStyle(this.maxTranslate);
        }
      }
    } else {
      if (this.yTranslate > 0) {
        if (this.yTranslate > -this.maxTranslate) {
          this.setStyle(this.maxTranslate);
        }
      } else {
        if (this.yTranslate < this.maxTranslate) {
          this.setStyle(-this.maxTranslate);
        }
      }
    }
    this.startObservation();
  }

  /*
   * Observes when the element's visibility changes
   *
   * this.isVisible: the element's parallax position will only update if this is true (element is visible in viewport)
   */
  intersect(entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.isVisible = true;
      } else {
        /*
         * When element is above or below the viewport lock the position to this.maxTranslate, so it is in the right position when it intersects again
         */
        this.isVisible = false;
        if (entry.boundingClientRect.top < 0) {
          this.isAbove = true;
          this.isBelow = false;
          this.setStyle(-this.maxTranslate);
        } else {
          this.isBelow = true;
          this.isAbove = false;
          this.setStyle(this.maxTranslate);
        }
      }
      this.emit('intersect');
    });
  }

  /*
   * Calculates the parallax transform for the current element
   */
  updateTransform(scrollY) {
    if (!this.isRecalculating) {
      this.isUpdating = true;

      let scrollOffset = scrollY - this.midPoint;
      let yTranslation = Math.round(scrollOffset * this.speed);
      this.setStyle(yTranslation);

      this.isUpdating = false;
    }
  }

  /*
   * Set a specific transform style on the element
   */
  setStyle(yTranslation) {
    var translate = 'translate3d(0px, ' + yTranslation + 'px, 0px)';
    this.yTranslate = yTranslation;
    this.element.style[transformProp] = translate;
  }
}
